// SPDX-License-Identifier: Apache-2.0
import GirafeSingleton from '../../base/GirafeSingleton';
import CustomTheme from '../../models/customtheme';
import BaseLayer from '../../models/layers/baselayer';
import { LayerId } from '../../models/layerdefinitions';
import GroupLayer from '../../models/layers/grouplayer';
import ILayerWithFilter from '../../models/layers/ilayerwithfilter';
import Layer from '../../models/layers/layer';
import LayerWms from '../../models/layers/layerwms';
import ThemeLayer from '../../models/layers/themelayer';
import { isTimeAwareLayer, TimeAwareLayer } from '../../models/layers/timeawarelayer';
import WfsFilterCondition, { isWfsOperator } from '../wfs/wfsfiltercondition';
import { KNOWN_FUNCTIONALITIES } from '../functionalities';
import WfsFilter from '../wfs/wfsfilter';

type LastSelectedTheme = ThemeLayer | CustomTheme | null;

export type LayerTreeChanges = {
  insertedLayers: BaseLayer[];
  activatedLayers: BaseLayer[];
};

export default class ThemesHelper extends GirafeSingleton {
  public override async initializeSingleton() {
    this.context.stateManager.subscribe(
      'themes.lastSelectedTheme',
      (_oldTheme: LastSelectedTheme, newTheme: LastSelectedTheme) => this.onSelectedThemeChanged(newTheme)
    );
  }

  private get state() {
    return this.context.stateManager.state;
  }

  public findBaseLayerById(layerId: LayerId): BaseLayer {
    for (const theme of Object.values(this.state.themes._allThemes)) {
      if (theme.id === layerId) {
        return theme;
      }

      const child = this.findBaseLayerRecursiveById(theme.children, layerId);
      if (child) {
        return child;
      }
    }

    throw new Error(`No BaseLayer with ID ${layerId} could be found.`);
  }

  public findBaseLayerRecursiveById(layers: BaseLayer[], layerId: LayerId): BaseLayer | null {
    for (const layer of layers) {
      if (layer.id === layerId) {
        return layer;
      }
      if (layer instanceof GroupLayer || layer instanceof ThemeLayer) {
        const child = this.findBaseLayerRecursiveById(layer.children, layerId);
        if (child) {
          return child;
        }
      }
    }

    return null;
  }

  public findThemeByName(themename: string): ThemeLayer | null {
    for (const theme of Object.values(this.state.themes._allThemes)) {
      if (theme.name === themename) {
        return theme;
      }
    }

    console.warn(`Layer ${themename} was found, but is not a group`);
    return null;
  }

  public findGroupByName(groupname: string): GroupLayer | null {
    const group = this.findBaseLayerByName(groupname);
    if (group instanceof GroupLayer) {
      return group;
    }

    console.warn(`Layer ${groupname} was found, but is not a group`);
    return null;
  }

  public findLayerByName(layername: string): Layer | null {
    const layer = this.findBaseLayerByName(layername);
    if (layer instanceof Layer) {
      return layer;
    }

    if (layer !== null) {
      console.warn(`Layer ${layername} was found, but is not a layer`);
    }
    return null;
  }

  private findBaseLayerByName(layername: string): BaseLayer | null {
    for (const theme of Object.values(this.state.themes._allThemes)) {
      const layer = this.findLayerRecursive(theme.children, layername);
      if (layer) {
        return layer;
      }
    }

    console.warn(`Layer ${layername} not found!`);
    return null;
  }

  private findLayerRecursive(layers: BaseLayer[], layername: string): BaseLayer | null {
    for (const layer of layers) {
      if (layer.name === layername) {
        return layer;
      }
      if (layer instanceof GroupLayer || layer instanceof ThemeLayer) {
        const child = this.findLayerRecursive(layer.children, layername);
        if (child) {
          return child;
        }
      }
    }

    return null;
  }

  /**
   * @param layer any layer or layer group (or theme) to find the related theme.
   * @returns The original theme configuration.
   */
  public getOriginalTheme(layer: BaseLayer): ThemeLayer | null {
    const hierarchy = this.getHierarchyFromLayer(layer);
    const theme = hierarchy?.length ? (hierarchy[0] as ThemeLayer) : undefined;
    return theme ? this.findThemeByName(theme.name) : null;
  }

  /**
   * Count the layer in both the given theme and the matching original theme.
   * @param theme the theme to check.
   * @returns true if the count is the same. False otherwise.
   */
  public isThemeComplete(theme: ThemeLayer): boolean {
    const originalTheme = this.getOriginalTheme(theme);
    if (!originalTheme) {
      return false;
    }
    const themeCount = this.countLayer(theme);
    const originalThemeCount = this.countLayer(originalTheme);
    return themeCount === originalThemeCount;
  }

  /**
   * @return the number of all sublayers (recursively) of the given layer.
   * @private
   */
  private countLayer(layer: BaseLayer): number {
    let count = 1;
    const layerAsUnknownGroup = layer as unknown as GroupLayer;
    if (layerAsUnknownGroup.children) {
      layerAsUnknownGroup.children.forEach((child) => {
        count += this.countLayer(child);
      });
    }
    return count;
  }

  /**
   * Add the related theme if it's not already (even partially) in the layertree. If it's
   * already in it, don't add it, warn the user, and highlight it.
   * @returns true if the related theme is added to the layertree, false otherwise.
   */
  public addThemeIfNotPresent(theme: ThemeLayer): boolean {
    const themeInLayersList = this.getThemeInLayersList(theme);

    // The theme is not yet added, add it!
    if (themeInLayersList === undefined) {
      if (this.state.themes.lastSelectedTheme?.id === theme.id) {
        this.state.themes.lastSelectedTheme = null;
      }
      this.state.themes.lastSelectedTheme = theme;
      return true;
    }

    // The theme is in the layer tree, then warn the user.
    themeInLayersList.isHighlighted = true;
    const layerName = this.context.i18nManager.getTranslation(theme.name);
    const msg = this.context.i18nManager
      .getTranslation('The theme {name} is already present in the treeview.')
      .replace('{name}', layerName);
    this.state.infobox.elements.push({
      id: theme.treeItemId,
      text: msg,
      type: 'info',
      duration: 5000
    });
    return false;
  }

  /**
   * Add the missing layers of an in-use theme, from an original theme description.
   * The added layers are deactivated.
   * @param originalTheme a Theme object, or model, as initially defined.
   */
  public completeTheme(originalTheme: ThemeLayer) {
    const clonedTheme = originalTheme.clone();
    this.deactivateAllLayers(clonedTheme);
    this.mergeLayerWithExistingLayerTree(clonedTheme, this.state.layers.layersList, false);
  }

  /**
   * Deactivate all layers and sublayer recursively.
   * Meaning state="off" and isDefaultChecked=false.
   * @private
   */
  private deactivateAllLayers(layer: BaseLayer) {
    layer.activeState = 'off';
    layer.isDefaultChecked = false;
    const layerAsUnknownGroup = layer as unknown as GroupLayer;
    if (layerAsUnknownGroup.children) {
      layerAsUnknownGroup.children.forEach((child) => {
        this.deactivateAllLayers(child);
      });
    }
  }

  private onSelectedThemeChanged(theme: ThemeLayer | CustomTheme | null) {
    if (!theme) {
      // Theme is null, nothing to do here
      return;
    }

    if (theme instanceof CustomTheme) {
      theme.layers.sort((a, b) => a.order - b.order);
      for (const t of theme.layers) {
        this.onThemeChanged(t);
      }
    } else {
      this.onThemeChanged(theme);
    }
  }

  private getThemeInLayersList(theme: ThemeLayer): BaseLayer | undefined {
    return this.state.layers.layersList.find((l) => l.id == theme.id);
  }

  private onThemeChanged(theme: ThemeLayer) {
    const themeInLayersList = this.getThemeInLayersList(theme);
    // Create a clone of the theme object to use it in the treeview.
    // This is essential, otherwise all changes are done in the layers
    // (For example, when expanding legend, expanding a group, or activating the layer).
    // Will also be done in the default layer configuration that has been loaded from themes.json,
    // And when a theme is selected aging from the themes-selector
    // The default configuration will have been overwritten.
    const clonedTheme = theme.clone();

    // Delete (replace) this theme if it already exists.
    if (themeInLayersList !== undefined) {
      this.context.stateManager.batchChanges(() => this.removeLayersFromLayerTree([themeInLayersList]));
      clonedTheme.order = themeInLayersList.order;
    }

    if (this.context.configManager.Config.themes.selectionMode === 'replace') {
      // Mode is <replace>
      this.emptyLayerTree();
      this.state.layers.layersList.push(clonedTheme);
    } else {
      // Mode is <add>
      this.state.layers.layersList.push(clonedTheme);
    }

    const themeFunctionalities = this.state.themes._allFunctionalities[theme.id];
    if (themeFunctionalities) {
      for (const functionality of Object.keys(themeFunctionalities)) {
        if (KNOWN_FUNCTIONALITIES.includes(functionality)) {
          this.state.functionalities[functionality] = themeFunctionalities[functionality];
        } else {
          console.warn(`Unknown functionality '${functionality}' found on Theme '${theme.name}'.`);
        }
      }
    }
  }

  public getMinimalClonedThemeForLayer(layer: BaseLayer): ThemeLayer {
    const hierarchy = this.getHierarchyFromLayer(layer);
    const theme = hierarchy[0] as ThemeLayer;
    const clone = theme.clone();
    // Remove unnecessary clones
    let children = clone.children;
    for (let i = 1; i < hierarchy.length; ++i) {
      this.removeOthers(children, hierarchy[i]);
      (children[0] as GroupLayer).isExpanded = true;
      children = (children[0] as GroupLayer).children;
    }

    const clonedLayer = this.findBaseLayerRecursiveById([clone], layer.id);
    if (clonedLayer) {
      clonedLayer.isHighlighted = true;
    }

    return clone;
  }

  private removeOthers(layers: BaseLayer[], keep: BaseLayer) {
    for (let i = layers.length - 1; i >= 0; --i) {
      if (layers[i].id !== keep.id) {
        layers.splice(i, 1);
      }
    }
  }

  private getHierarchyFromLayer(layer: BaseLayer): BaseLayer[] {
    if (layer instanceof ThemeLayer) {
      return [layer];
    }
    if (!layer.parent) {
      throw new Error('A group or a layer should always have a parent.');
    }
    const parents = this.getHierarchyFromLayer(layer.parent);
    return [...parents, layer];
  }

  public addThemesFromUrl() {
    let themeAdded = false;
    if (this.context.permalinkManager.hasThemes()) {
      for (const themename of this.context.permalinkManager.getThemes()) {
        const theme = Object.values(this.state.themes._allThemes).find((t) => t.name === themename);
        if (theme) {
          this.state.themes.lastSelectedTheme = theme;
          themeAdded = true;
        } else {
          console.warn(`Theme ${themename} cannot be found`);
        }
      }
    }
    return themeAdded;
  }

  public addGroupsFromUrl(): boolean {
    let added = false;
    if (this.context.permalinkManager.hasGroups()) {
      for (const groupname of this.context.permalinkManager.getGroups()) {
        added = this.addLayerBaseFromName(groupname, 'group') || added;
      }
    }
    return added;
  }

  public addLayersFromUrl(): boolean {
    let added = false;
    if (this.context.permalinkManager.hasLayers()) {
      for (const layername of this.context.permalinkManager.getLayers()) {
        added = this.addLayerBaseFromName(layername, 'layer') || added;
      }
    }
    return added;
  }

  public addLayerFromName(layername: string): boolean {
    return this.addLayerBaseFromName(layername, 'layer');
  }

  private addLayerBaseFromName(layer: string, type: 'layer' | 'group'): boolean {
    const layerOptions = this.extractLayerOptions(layer, type);
    if (!layerOptions) {
      console.warn(`Layer ${layer} cannot be found`);
      return false;
    }

    const clonedTheme = this.getMinimalClonedThemeForLayer(layerOptions.originalLayer);
    this.mergeLayerWithExistingLayerTree(clonedTheme, this.state.layers.layersList);
    if (!layerOptions.active) {
      // Layer added, but inactive. No further config needed
      return true;
    }

    const clonedLayer = this.findLayerRecursive(this.state.layers.layersList, layerOptions.originalLayer.name);
    if (clonedLayer) {
      this.context.layerManager.toggle(clonedLayer, 'on');
      if (layerOptions.opacity) {
        (clonedLayer as Layer).opacity = layerOptions.opacity;
      }
      if (layerOptions.filter) {
        (clonedLayer as unknown as ILayerWithFilter).filter = layerOptions.filter;
      }
      if (layerOptions.timeRestriction) {
        (clonedLayer as TimeAwareLayer).timeRestriction = layerOptions.timeRestriction;
      }
    }
    return true;
  }

  private extractLayerOptions(urlParam: string, type: 'layer' | 'group') {
    let active = true;
    if (urlParam.startsWith('!')) {
      active = false;
      urlParam = urlParam.substring(1);
    }

    const layerOptions = urlParam.split('|');
    const layername = layerOptions[0];
    const layerOrGroup = type === 'layer' ? this.findLayerByName(layername) : this.findGroupByName(layername);
    if (!layerOrGroup) {
      // No layer found
      return;
    }
    let opacity = undefined;
    let filter = undefined;
    let timeRestriction = undefined;
    for (let i = 1; i < layerOptions.length; ++i) {
      const option = layerOptions[i];
      if (option.startsWith('o;')) {
        opacity = this.extractLayerOptionOpacity(layerOrGroup, option);
      } else if (option.startsWith('f;')) {
        filter = this.extractLayerOptionFilter(layerOrGroup, option);
      } else if (option.startsWith('t;')) {
        timeRestriction = this.extractLayerOptionTime(layerOrGroup, option);
      }
    }

    return {
      originalLayer: layerOrGroup,
      active: active,
      opacity: opacity,
      filter: filter,
      timeRestriction: timeRestriction
    };
  }

  private extractLayerOptionOpacity(layerOrGroup: Layer | GroupLayer, option: string): number | undefined {
    if (!(layerOrGroup instanceof Layer)) {
      console.warn('Permalink: opacity configuration is only allowed for layers.');
      return;
    }
    return Number(option.substring(2));
  }

  private extractLayerOptionFilter(layerOrGroup: Layer | GroupLayer, option: string): WfsFilter | undefined {
    if (!(layerOrGroup instanceof Layer)) {
      console.warn('Permalink: filter configuration is only allowed for layers.');
      return;
    }
    if (!(layerOrGroup instanceof LayerWms)) {
      console.warn('Permalink: filter configuration is only allowed for layers that support filters.');
      return;
    }
    const filterParams = option.substring(2).split(';');
    const filterProperty = filterParams[0];
    const filterOperator = filterParams[1];
    if (!isWfsOperator(filterOperator)) {
      console.warn('Permalink: filter operator is unknown.');
      return;
    }
    const filterValue = filterParams[2];
    const filterPropertyType = filterParams.length > 3 ? filterParams[3] : 'string';

    // Note: Only one condition is supported at the moment, no chained or nested conditions
    return new WfsFilter([
      new WfsFilterCondition(filterProperty, filterOperator, filterValue, undefined, filterPropertyType)
    ]);
  }

  private extractLayerOptionTime(layerOrGroup: Layer | GroupLayer, option: string): string | undefined {
    if (!isTimeAwareLayer(layerOrGroup)) {
      console.warn('Permalink: time configuration is only allowed for layers that support time configuration.');
      return;
    }
    return option.substring(2);
  }

  /**
   * Calculates the initial order (=position) for a new theme in the tree.
   * Themes are added at the top of the tree, but underneath any pinned themes. Defaults to 0.
   * This prevents themes of visibly "jumping" around in the tree after being added due to reordering.
   */
  public getInitialOrderForNewTheme(): number {
    return this.state.layers.layersList
      .filter((l) => l.isPinned)
      .map((l) => l.order)
      .reduce((a, b) => Math.max(a, b), 0);
  }

  public mergeThemeInLayerTree(
    theme: ThemeLayer,
    activate: boolean = false,
    forceTop: boolean = false
  ): LayerTreeChanges {
    theme.order = forceTop ? -1 : this.getInitialOrderForNewTheme();
    const layerTreeChanges = { insertedLayers: [], activatedLayers: [] };
    this.mergeLayerWithExistingLayerTree(theme, this.state.layers.layersList, activate, layerTreeChanges);
    return layerTreeChanges;
  }

  /**
   * This function merges the newLayer at its right place in the LayerTree
   * @param newLayer The layer to insert somewhere in the hierarchy
   * @param existingList
   * @param parent
   * @returns
   */
  private mergeLayerWithExistingLayerTree(
    newLayer: BaseLayer,
    existingList: BaseLayer[],
    activate: boolean = false,
    layerTreeChanges?: LayerTreeChanges,
    parent?: GroupLayer | ThemeLayer
  ) {
    const existingLayer = existingList.find((l) => l.id === newLayer.id);
    if (!existingLayer) {
      // The theme is not already present. We just add the theme to the layertree
      this.addLayerToLayerTree(newLayer, existingList, activate, layerTreeChanges, parent);
      return;
    }

    if (newLayer.isHighlighted) {
      // The layer is already present
      this.highlightLayerInLayerTree(existingLayer, activate, layerTreeChanges);
      return;
    }

    // Otherwise, we have to merge the themes/groups
    if (
      (newLayer instanceof ThemeLayer && existingLayer instanceof ThemeLayer) ||
      (newLayer instanceof GroupLayer && existingLayer instanceof GroupLayer)
    ) {
      for (const child of newLayer.children) {
        this.mergeLayerWithExistingLayerTree(child, existingLayer.children, activate, layerTreeChanges, existingLayer);
      }
    }
  }

  private highlightLayerInLayerTree(existingLayer: BaseLayer, activate: boolean, layerTreeChanges?: LayerTreeChanges) {
    existingLayer.isHighlighted = true;
    if (existingLayer.parent) {
      existingLayer.parent.isExpanded = true;
    }
    if (activate && existingLayer.inactive) {
      this.context.layerManager.toggle(existingLayer, 'on');
      if (layerTreeChanges) {
        layerTreeChanges.activatedLayers.push(existingLayer);
      }
    }
  }

  private addLayerToLayerTree(
    newLayer: BaseLayer,
    existingList: BaseLayer[],
    activate: boolean = false,
    layerTreeChanges?: LayerTreeChanges,
    parent?: GroupLayer | ThemeLayer
  ) {
    if (parent) {
      newLayer.parent = parent;
    }
    this.insertLayerToLayerTreeAtInitialPosition(newLayer, existingList);
    if (activate) {
      this.context.layerManager.toggle(newLayer, 'on');
    }
    if (layerTreeChanges) {
      layerTreeChanges.insertedLayers.push(newLayer);
    }
  }

  public removeLayersFromLayerTree(layersToRemove: BaseLayer[]) {
    for (const layerToRemove of layersToRemove) {
      this.context.layerManager.toggle(layerToRemove, 'off');
      this.removeLayersFromExistingLayerTree(layerToRemove, this.state.layers.layersList);
    }
  }

  private removeLayersFromExistingLayerTree(layerToRemove: BaseLayer, existingList: BaseLayer[]) {
    const existingLayerIndex = existingList.findIndex((l) => l.id === layerToRemove.id);
    if (existingLayerIndex >= 0) {
      existingList.splice(existingLayerIndex, 1);
    } else {
      for (const element of existingList) {
        if (element instanceof ThemeLayer || element instanceof GroupLayer) {
          this.removeLayersFromExistingLayerTree(layerToRemove, element.children);
        }
      }
    }
  }

  /**
   * Find the initial position of the layer (relative to its sibling layers) by looking
   * at the original theme object and insert the layer following this original order.
   * (The "layer.order" value is incremented with the user operations, that's why we can
   * rely on it for new layers and must adapt it to the existing sibling layers.)
   * @private
   */
  private insertLayerToLayerTreeAtInitialPosition(newLayer: BaseLayer, existingList: BaseLayer[]) {
    const originalLayer = this.findBaseLayerById(newLayer.id);
    const originalParent = originalLayer.parent;
    if (!originalParent || existingList.length === 0) {
      existingList.push(newLayer);
      return;
    }
    // Following the original order, find the nearest previous or next sibling layer in the existingList.
    const originalSiblings = originalParent.children;
    const existingListIds = new Set(existingList.map((layer) => layer.id));
    let previousOriginalSibling: BaseLayer | undefined;
    let nextOriginalSibling: BaseLayer | undefined;
    let originalLayerEncountered = false;
    originalSiblings.some((oLayer) => {
      if (originalLayerEncountered && (previousOriginalSibling || nextOriginalSibling)) {
        return true;
      }
      if (oLayer.id === newLayer.id) {
        originalLayerEncountered = true;
      } else if (existingListIds.has(oLayer.id)) {
        if (originalLayerEncountered) {
          nextOriginalSibling = oLayer;
        } else {
          previousOriginalSibling = oLayer;
        }
      }
    });
    // Find back the matching sibling layer in the ExistingList and update the "newLayer.order"
    // to be just before or just after the sibling layer.
    if (previousOriginalSibling) {
      const previousSibling = existingList.find((layer) => layer.id === previousOriginalSibling?.id);
      newLayer.order = previousSibling?.order ? previousSibling.order : newLayer.order;
    } else if (nextOriginalSibling) {
      const nextSibling = existingList.find((layer) => layer.id === nextOriginalSibling?.id);
      newLayer.order = nextSibling?.order ? nextSibling.order - 1 : newLayer.order;
    }
    // Push the layer in the list. The orderManager will move the layer regarding the order values.
    existingList.push(newLayer);
  }

  /**
   * Removes all layers from the layer tree except themes that are pinned.
   */
  public emptyLayerTree() {
    const layersToRemove = this.state.layers.layersList.filter((l) => !l.isPinned);
    this.context.stateManager.batchChanges(() => this.removeLayersFromLayerTree(layersToRemove));
  }
}
